import React, { useEffect } from 'react';
import { Button, Field, Input, Spinner } from '@billda/design-system';
import { Controller, useForm } from 'react-hook-form';
import { RHFPhoneInput } from '../../../shared/components/forms/RHFPhoneInput';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useGetCompanyDetails,
  useUpsertCompanyDetails,
} from '../hooks/useCompanyDetails';
import {
  companyDetailsSchema,
  CompanyDetailsFormData,
  defaultCompanyDetailsValues,
} from '../types/companyDetailsSchema';
import { useSnackbarStore } from '../../../shared/stores/snackbarStore';

interface CompanyDetailsFormProps {
  readOnly?: boolean;
}

export const CompanyDetailsForm: React.FC<CompanyDetailsFormProps> = ({
  readOnly = false,
}) => {
  const { data: companyDetails } = useGetCompanyDetails();
  const upsertMutation = useUpsertCompanyDetails();
  const showSnackbar = useSnackbarStore(state => state.showSnackbar);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isDirty },
  } = useForm<CompanyDetailsFormData>({
    defaultValues: defaultCompanyDetailsValues,
    resolver: yupResolver(companyDetailsSchema),
    mode: 'onChange',
  });

  useEffect(() => {
    if (companyDetails) {
      reset({
        companyName: companyDetails.companyName,
        companyNumber: companyDetails.companyNumber,
        addressLine1: companyDetails.addressLine1,
        addressLine2: companyDetails.addressLine2,
        city: companyDetails.city,
        county: companyDetails.county,
        postcode: companyDetails.postcode,
        country: companyDetails.country,
        companyEmail: companyDetails.companyEmail,
        companyPhone: companyDetails.companyPhone,
      });
    }
  }, [companyDetails, reset]);

  const onSubmit = (data: CompanyDetailsFormData) => {
    upsertMutation.mutate(
      {
        companyName: data.companyName,
        companyNumber: data.companyNumber || null,
        addressLine1: data.addressLine1,
        addressLine2: data.addressLine2 || null,
        city: data.city,
        county: data.county || null,
        postcode: data.postcode,
        country: data.country || null,
        companyEmail: data.companyEmail,
        companyPhone: data.companyPhone,
      },
      {
        onSuccess: () => {
          showSnackbar('Company details saved', 'success');
        },
      }
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col gap-5">
        <Controller
          name="companyName"
          control={control}
          render={({ field, fieldState }) => (
            <Field
              label="Company Name"
              required={!readOnly}
              error={!readOnly ? fieldState.error?.message : undefined}
            >
              <Input
                {...field}
                readOnly={readOnly}
                invalid={!readOnly && !!fieldState.error}
                data-testid="company-name-input"
              />
            </Field>
          )}
        />

        <Controller
          name="companyNumber"
          control={control}
          render={({ field, fieldState }) => (
            <Field
              label="Company Number"
              error={!readOnly ? fieldState.error?.message : undefined}
            >
              <Input
                {...field}
                value={field.value ?? ''}
                readOnly={readOnly}
                invalid={!readOnly && !!fieldState.error}
                data-testid="company-number-input"
              />
            </Field>
          )}
        />

        <Controller
          name="addressLine1"
          control={control}
          render={({ field, fieldState }) => (
            <Field
              label="Address Line 1"
              required={!readOnly}
              error={!readOnly ? fieldState.error?.message : undefined}
            >
              <Input
                {...field}
                readOnly={readOnly}
                invalid={!readOnly && !!fieldState.error}
                data-testid="address-line1-input"
              />
            </Field>
          )}
        />

        <Controller
          name="addressLine2"
          control={control}
          render={({ field, fieldState }) => (
            <Field
              label="Address Line 2"
              error={!readOnly ? fieldState.error?.message : undefined}
            >
              <Input
                {...field}
                value={field.value ?? ''}
                readOnly={readOnly}
                invalid={!readOnly && !!fieldState.error}
                data-testid="address-line2-input"
              />
            </Field>
          )}
        />

        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="flex-1">
            <Controller
              name="city"
              control={control}
              render={({ field, fieldState }) => (
                <Field
                  label="City"
                  required={!readOnly}
                  error={!readOnly ? fieldState.error?.message : undefined}
                >
                  <Input
                    {...field}
                    readOnly={readOnly}
                    invalid={!readOnly && !!fieldState.error}
                    data-testid="city-input"
                  />
                </Field>
              )}
            />
          </div>

          <div className="flex-1">
            <Controller
              name="county"
              control={control}
              render={({ field, fieldState }) => (
                <Field
                  label="County"
                  error={!readOnly ? fieldState.error?.message : undefined}
                >
                  <Input
                    {...field}
                    value={field.value ?? ''}
                    readOnly={readOnly}
                    invalid={!readOnly && !!fieldState.error}
                    data-testid="county-input"
                  />
                </Field>
              )}
            />
          </div>
        </div>

        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="flex-1">
            <Controller
              name="postcode"
              control={control}
              render={({ field, fieldState }) => (
                <Field
                  label="Postcode"
                  required={!readOnly}
                  error={!readOnly ? fieldState.error?.message : undefined}
                >
                  <Input
                    {...field}
                    readOnly={readOnly}
                    invalid={!readOnly && !!fieldState.error}
                    data-testid="postcode-input"
                  />
                </Field>
              )}
            />
          </div>

          <div className="flex-1">
            <Controller
              name="country"
              control={control}
              render={({ field, fieldState }) => (
                <Field
                  label="Country"
                  error={!readOnly ? fieldState.error?.message : undefined}
                >
                  <Input
                    {...field}
                    value={field.value ?? ''}
                    readOnly={readOnly}
                    invalid={!readOnly && !!fieldState.error}
                    data-testid="country-input"
                  />
                </Field>
              )}
            />
          </div>
        </div>

        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="flex-1">
            <Controller
              name="companyEmail"
              control={control}
              render={({ field, fieldState }) => (
                <Field
                  label="Company Email"
                  required={!readOnly}
                  error={!readOnly ? fieldState.error?.message : undefined}
                >
                  <Input
                    {...field}
                    type="email"
                    readOnly={readOnly}
                    invalid={!readOnly && !!fieldState.error}
                    data-testid="company-email-input"
                  />
                </Field>
              )}
            />
          </div>

          <div className="flex-1">
            <RHFPhoneInput
              name="companyPhone"
              control={control}
              label="Company Phone"
              fullWidth
              required={!readOnly}
              disabled={readOnly}
              data-testid="company-phone-input"
            />
          </div>
        </div>

        {!readOnly && (
          <div className="flex justify-end">
            <Button
              variant="neutral"
              type="submit"
              disabled={!isValid || !isDirty || upsertMutation.isPending}
              data-testid="save-company-details-button"
            >
              {upsertMutation.isPending ? (
                <>
                  <Spinner size={20} className="mr-2" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
          </div>
        )}
      </div>
    </form>
  );
};
